/**
 * Cross-browser HTML printing via a real popup window.
 *
 * Flow: open print page → print dialog → after print/cancel, close that page
 * and return to the app.
 *
 * Async printers (logo fetch, etc.) must call `openPrintWindow()` *before*
 * any `await`, then pass that window as the second argument to `printHtml`.
 */

/**
 * Open a blank print window. Call this synchronously from a click/tap handler
 * *before* any `await`, then pass the result to `printHtml(html, win)`.
 * @returns {Window|null}
 */
export function openPrintWindow() {
  try {
    // Do NOT use "noopener" — we need the Window reference to write + print.
    return window.open("", "_blank");
  } catch {
    return null;
  }
}

function cleanupWindow(win) {
  try {
    if (win && !win.closed) win.close();
  } catch {
    /* ignore */
  }
  // The popup had focus for printing; closing it doesn't always hand focus
  // back to the opener (seen on Chrome/Windows), leaving the POS page looking
  // normal but not accepting clicks until the user alt-tabs. Force it back.
  try {
    window.focus();
  } catch {
    /* ignore */
  }
}

/** Close as soon as the print dialog is done (or user returns to the app). */
function armPrintWindowClose(win) {
  if (!win || win.closed) return;
  let closed = false;
  const closeSoon = (ms = 400) => {
    if (closed) return;
    closed = true;
    setTimeout(() => cleanupWindow(win), ms);
  };

  try {
    win.addEventListener("afterprint", () => closeSoon(400), { once: true });
  } catch {
    /* ignore */
  }
  try {
    if (typeof win.matchMedia === "function") {
      const mql = win.matchMedia("print");
      const onChange = (e) => {
        if (!e.matches) {
          try { mql.removeEventListener("change", onChange); } catch { /* ignore */ }
          closeSoon(400);
        }
      };
      if (typeof mql.addEventListener === "function") mql.addEventListener("change", onChange);
      else if (typeof mql.addListener === "function") mql.addListener(onChange);
    }
  } catch {
    /* ignore */
  }
  // Safety net if nothing else fires.
  setTimeout(() => cleanupWindow(win), 90000);
}

/**
 * Resolves once every <img> in the print window has finished loading (or
 * failed) — logos are often on a different domain than the app, so a fixed
 * short delay can fire print() before they've downloaded, leaving a blank
 * space on the receipt. Capped so a broken/slow image never blocks printing.
 */
function waitForImages(win, maxWaitMs = 1500) {
  return new Promise((resolve) => {
    let imgs;
    try {
      imgs = win?.document?.images ? Array.from(win.document.images) : [];
    } catch {
      imgs = [];
    }
    const pending = imgs.filter((img) => !img.complete);
    if (pending.length === 0) {
      resolve();
      return;
    }
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    let remaining = pending.length;
    pending.forEach((img) => {
      const onSettle = () => {
        remaining -= 1;
        if (remaining <= 0) done();
      };
      img.addEventListener("load", onSettle, { once: true });
      img.addEventListener("error", onSettle, { once: true });
    });
    setTimeout(done, maxWaitMs);
  });
}

function printInWindow(win, html) {
  try {
    win.document.open();
    win.document.write(html);
    win.document.close();
  } catch {
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      /* ignore */
    }
  }

  armPrintWindowClose(win);

  let printed = false;
  const trigger = () => {
    if (printed || !win || win.closed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
  };
  const triggerAfterImages = () => {
    waitForImages(win).then(trigger);
  };

  try {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(triggerAfterImages));
    } else {
      triggerAfterImages();
    }
  } catch {
    triggerAfterImages();
  }
  setTimeout(trigger, 2000);
}

/** Last resort when popups are blocked. */
function printViaIframe(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.style.cssText =
    "position:fixed;left:-9999px;top:0;width:794px;height:1123px;border:0;opacity:0;pointer-events:none;";

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try {
      if (iframe.parentNode) document.body.removeChild(iframe);
    } catch {
      /* ignore */
    }
  };

  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    try {
      win.onafterprint = cleanup;
    } catch {
      /* ignore */
    }
    waitForImages(win).then(() => {
      try {
        win.focus();
        win.print();
      } catch {
        /* ignore */
      }
    });
    setTimeout(cleanup, 60000);
  };

  iframe.srcdoc = html;
  document.body.appendChild(iframe);
}

/**
 * @param {string} html - Complete HTML document string.
 * @param {Window|null} [existingWin] - Optional window from `openPrintWindow()`
 *   (required when the caller `await`s before printing).
 */
export function printHtml(html, existingWin = null) {
  if (existingWin && !existingWin.closed) {
    printInWindow(existingWin, html);
    return;
  }

  const win = openPrintWindow();
  if (win) {
    printInWindow(win, html);
    return;
  }

  try {
    window.alert(
      "Please allow pop-ups for this site so print preview can open."
    );
  } catch {
    /* ignore */
  }
  printViaIframe(html);
}

export default printHtml;
